import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PushCard } from "@/components/PushCard";
import { DemoRunner } from "@/components/DemoRunner";
import { TodayBlock } from "@/components/TodayBlock";
import { TutorialWizard } from "@/components/TutorialWizard";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { Button } from "@/components/ui/button";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  Users, MessageCircle, CalendarCheck, CalendarClock, Loader2,
  Smartphone, Sparkles, ArrowUpRight, ClipboardList,
} from "lucide-react";

const fmt = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [company, setCompany] = useState(null);
  const [tutorial, setTutorial] = useState(null);
  const [showTut, setShowTut] = useState(true);
  const navigate = useNavigate();

  const loadStats = () => api.get("/dashboard/stats").then(({ data }) => setStats(data)).catch(() => {});
  useEffect(() => {
    loadStats();
    api.get("/company").then(({ data }) => setCompany(data)).catch(() => {});
    api.get("/tutorial").then(({ data }) => setTutorial(data.tutorial)).catch(() => {});
  }, []);

  if (!stats) return <div className="grid h-[60vh] place-items-center"><Loader2 className="h-8 w-8 animate-spin text-cyan-400" /></div>;

  const metrics = [
    { label: "Total de clientes", value: stats.total_clients, icon: Users, testid: "dashboard-metric-total-clients", color: "text-cyan-400" },
    { label: "Mensagens hoje", value: stats.messages_today, icon: MessageCircle, testid: "dashboard-metric-messages-today", color: "text-blue-400" },
    { label: "Agendamentos hoje", value: stats.appointments_today, icon: CalendarCheck, testid: "dashboard-metric-appointments-today", color: "text-emerald-400" },
    { label: "Próximos agendamentos", value: stats.appointments_upcoming, icon: CalendarClock, testid: "dashboard-metric-upcoming", color: "text-amber-400" },
  ];
  const connected = stats.whatsapp_status === "conectado";

  return (
    <div className="mx-auto max-w-7xl space-y-7" data-testid="dashboard-page">
      {company?.onboarded === false && <OnboardingDialog company={company} onFinished={() => { setCompany((c) => ({ ...c, onboarded: true })); loadStats(); }} />}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 inline-flex items-center gap-2 text-xs font-mono-flow uppercase tracking-widest text-cyan-400"><Sparkles className="h-3.5 w-3.5" /> Visão geral</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight text-white sm:text-4xl">Dashboard</h1>
        </div>
        <div className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm ${connected ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-300" : "border-amber-500/30 bg-amber-500/10 text-amber-300"}`} data-testid="dashboard-wa-status">
          <Smartphone className="h-4 w-4" /> WhatsApp {connected ? "conectado" : "desconectado"}
        </div>
      </div>

      {tutorial && !tutorial.finished && !tutorial.skipped && showTut && company?.onboarded !== false && <TutorialWizard onClose={() => setShowTut(false)} />}

      <TodayBlock today={stats.today} next={stats.next_appointment} assist={stats.ai_mode === "assist"} />

      {(stats.ai_mode === "assist" || stats.requests?.open > 0) && (
        <button type="button" onClick={() => navigate("/pedidos")} className="glow-card flex w-full items-center justify-between gap-4 rounded-2xl border-amber-400/30 p-5 text-left transition-transform hover:-translate-y-0.5" data-testid="dashboard-requests-card">
          <div className="flex items-center gap-4">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-amber-400/15 text-amber-300"><ClipboardList className="h-6 w-6" /></div>
            <div>
              <p className="font-display text-3xl font-extrabold text-white" data-testid="dashboard-requests-open">{stats.requests?.open ?? 0}</p>
              <p className="text-sm text-slate-400">Pedidos aguardando seu retorno{stats.requests?.new_today ? ` · ${stats.requests.new_today} novo(s) hoje` : ""}</p>
            </div>
          </div>
          <span className="text-sm font-medium text-cyan-300">Ver pedidos →</span>
        </button>
      )}

      <DemoRunner onDone={loadStats} />

      {/* Metrics */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map((m) => (
          <div key={m.label} className="glow-card animate-flow-in rounded-2xl p-5" data-testid={m.testid}>
            <div className="flex items-center justify-between">
              <div className={`grid h-11 w-11 place-items-center rounded-xl bg-slate-800/70 ${m.color}`}><m.icon className="h-5 w-5" /></div>
              <ArrowUpRight className="h-4 w-4 text-slate-600" />
            </div>
            <p className="mt-4 font-display text-4xl font-extrabold text-white">{m.value}</p>
            <p className="mt-1 text-sm text-slate-400">{m.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Chart */}
        <div className="glow-card rounded-2xl p-5 lg:col-span-2" data-testid="dashboard-chart-ai-activity">
          <h3 className="font-display text-lg font-semibold text-white">Mensagens processadas pela IA (7 dias)</h3>
          <div className="mt-4 h-64 min-h-[256px] w-full min-w-0">
            <ResponsiveContainer width="100%" height={256}>
              <BarChart data={stats.chart}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E293B" vertical={false} />
                <XAxis dataKey="day" stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#0F172A", border: "1px solid #1E293B", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(0,240,255,0.06)" }} />
                <Bar dataKey="messages" fill="#00F0FF" radius={[6, 6, 0, 0]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Activity + push */}
        <div className="space-y-5">
          <PushCard />

          <div className="glow-card rounded-2xl p-5">
            <h3 className="font-display text-lg font-semibold text-white">Atividade recente</h3>
            <div className="mt-3 space-y-3">
              {stats.recent.length === 0 && <p className="text-sm text-slate-500">Sem atividade ainda.</p>}
              {stats.recent.map((n) => (
                <div key={n.id} className="flex items-start gap-3 rounded-xl border border-slate-800/60 bg-slate-900/40 p-3" data-testid="dashboard-activity-item">
                  <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-cyan-400 pulse-ring" />
                  <div>
                    <p className="text-sm font-medium text-slate-100">{n.title}</p>
                    <p className="text-xs text-slate-400">{n.body}</p>
                    <p className="mt-0.5 text-[11px] text-slate-600">{fmt(n.created_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3">
        <Button onClick={() => navigate("/inbox")} variant="outline" className="border-slate-700 bg-slate-900/50 text-slate-200 hover:bg-slate-800" data-testid="dashboard-go-inbox">Abrir Inbox</Button>
        <Button onClick={() => navigate("/agenda")} variant="outline" className="border-slate-700 bg-slate-900/50 text-slate-200 hover:bg-slate-800" data-testid="dashboard-go-agenda">Ver Agenda</Button>
      </div>
    </div>
  );
}
